import { useSelector } from "react-redux";
import { colors } from "../../../data/colors";
import { RootState } from "../../../store/store";
import { useTranslation } from "react-i18next";
import { getRealCode } from "../../../utils/getRealCode";
import { convertCellData } from "../../../utils/convertCellData";
import { userTypes } from "../../../data/types/userTypes";
import writeListTable from "./writeListTable";
import PaidIcon from "@mui/icons-material/Paid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NumbersIcon from "@mui/icons-material/Numbers";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import InventoryIcon from "@mui/icons-material/Inventory";
import EventIcon from "@mui/icons-material/Event";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import LanguageIcon from "@mui/icons-material/Language";
import PrintIcon from "@mui/icons-material/Print";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import NOPIC from "../../../assets/nopic.svg";
import { useStructureContext } from "../../../context/StructureContext";
import { stdObject } from "../../../data/types/stdObject";
import { arrayToObject } from "../../../utils/arrayToObject";
import { sexesOptions } from "../SearchBar/data/searchItemUtils";
import { ageToString } from "../../../utils/ageToString";
import { BoxRow } from "./BoxRow";
import { useMainContext } from "../../../context/MainContext";
import { mainConfig, purchasesCodes, salesCodes } from "../../../data/main";
import { openPrinterDialog } from "../../MainFrame/functions/stationery/openPrinterDialog";
import convertDate from "../../../utils/convertDate";
import { convertAge } from "../../../utils/convertAge";
// import { searchList } from "../functions/searchList";

const statusColor = (value: number) =>
  value === 1 ? colors.orange : value === 2 ? colors.green : colors.grey;

export default function ListBox() {
  const { setStaValues, setStationeryModal, setMsg, closeMsg, logoutAction } =
    useMainContext();
  const {
    scrollDiv,
    handleScroll,
    type,
    section,
    headers,
    boxList,
    categories,
    openRowInfo,
  } = useStructureContext();
  const { t } = useTranslation();
  const user: userTypes = useSelector((state: RootState) => state.data.user);
  const usersList: stdObject = useSelector(
    (state: RootState) => state.data.usersList,
  );
  const routesList: stdObject = useSelector(
    (state: RootState) => state.data.routes,
  );
  const userAccess: stdObject = useSelector(
    (state: RootState) => state.data.userAccess,
  );
  const pricesRates: any = useSelector((state: RootState) => state.data.tar);
  const warehouses: any = useSelector((state: RootState) => state.data.alm);
  const searchOptions: any = useSelector(
    (state: RootState) => state.data.search,
  );
  const statusDict: stdObject = arrayToObject({
    key: "CODPAG",
    erase: ["CODPAG"],
    data: searchOptions.docStatus,
  });
  const insurancesList: stdObject = arrayToObject({
    key: "CODPSE",
    erase: ["CODPSE"],
    data: searchOptions.docInsurances,
  });

  const mainHeader =
    section.code in headers
      ? Object.keys(headers[section.code]).length === 0
        ? []
        : [
            ...(headers[section.code].user.length > 0
              ? headers[section.code].user
              : headers[section.code].default
            )
              .filter((e: any) => e.ACTCAB === 1)
              .sort((a: any, b: any) => b.ORDCAB - a.ORDCAB),
            ...([...purchasesCodes, ...salesCodes].includes(section.code) &&
            userAccess[section.id[0]][`${section.id}P`]
              ? [{ ACTCAB: 1, CODCAB: "PRINT", ALICAB: 2 }]
              : []),
          ]
      : [];

  const writeInfo = (key: string, data: stdObject) => {
    let tempData = convertCellData({
      key,
      section: typeof section.code === "string" ? section.code : "",
      data:
        key === "TIMCLI"
          ? { start: data.NACCLI, end: false }
          : key === "TIMSEV" && data.CODCLI
            ? {
                start: data.NACCLI,
                end: data.FECSEV,
              }
            : data,
      country: user.CODPAI,
      t,
    });
    if (typeof tempData === "object") {
      switch (tempData.code) {
        case "IMG":
          return (
            <div className="listImage">
              <img
                src={
                  tempData.data !== ""
                    ? `${mainConfig.url}/images/ART/small/${
                        data.IMGUART !== "" ? data.IMGUART : data.CODART
                      }.${tempData.data}?v=${data.UPTART}`
                    : NOPIC
                }
                onError={(e: any) => (e.target.src = NOPIC)}
                loading="lazy"
                alt={data.CODART}
              />
            </div>
          );
        case "ADJ":
          const { Icon, color } = tempData;
          return Icon === null ? (
            <div style={{ width: 20, height: 25 }} />
          ) : (
            <Icon style={{ fontSize: 20, color }} />
          );
        case "TIM":
          return tempData.data ? ageToString(tempData.data, t) : "";
        case "PRE":
          return typeof tempData.data === "object"
            ? writeListTable({
                columns: pricesRates,
                title: "NOMTAR",
                data: tempData.data,
                type: "TAR",
              })
            : [];
        case "ALM":
          return [1, 5].includes(data.TIPART)
            ? ""
            : typeof tempData.data === "object"
              ? writeListTable({
                  columns: warehouses,
                  title: "NOMALM",
                  data: tempData.data,
                  type: "ALM",
                  warehouse: user.ALMSUC,
                })
              : [];
        case "DOM":
          return typeof tempData.data === "object"
            ? writeListTable({
                columns: tempData.data,
                type: "DOM",
              })
            : [];
        case "ITEMS":
          return (
            <table className="itemsTable">
              {tempData.data.map((e: string[]) => (
                <tr>
                  <td valign="top">{e[2]}</td>
                  <td>{`[${e[0]}] ${e[1]}`}</td>
                </tr>
              ))}
            </table>
          );
        default:
          return JSON.stringify(tempData);
      }
    } else {
      const code = getRealCode(
        key,
        typeof section.code === "string" ? section.code : "",
      );
      switch (code) {
        case "FECDOC":
          const fecData = tempData.split(";");
          if (fecData.length === 2) {
            fecData[1] = fecData[1].split(".");
            if (fecData[1].length === 2) {
              fecData[1] = t(`c.${fecData[1][0]}`) + " " + fecData[1][1];
              const newAge = convertAge(fecData[0].split(" ")?.[0]);
              return (
                <>
                  <div>{fecData[1]}</div>
                  <div>
                    {Array.isArray(newAge) ? ageToString(newAge, t) : ""}
                  </div>
                </>
              );
            }
          }
          return "";

        case "ALM":
        case "ALM2":
          return (
            warehouses.find((e: stdObject) => e.CODALM === Number(tempData))
              ?.NOMALM ?? tempData
          );
        case "FAM":
          let catValue = categories.filter(
            (e: stdObject) => e.CODCAT === Number(tempData),
          );
          return catValue.length === 1
            ? catValue[0].NOMCAT
            : tempData.toString() === "0"
              ? ""
              : tempData;
        case "TELPRO":
        case "CELPRO":
        case "EMA1PRO":
        case "TELCLI":
        case "CELCLI":
        case "EMA1CLI":
        case "RUTCLI":
          return tempData ?? "";
        case "NACCLI":
          if (typeof tempData === "string") {
            const nacData = tempData.split("-");
            const newAge2 = convertAge(tempData);
            if (
              nacData.length === 3 &&
              nacData.every((v: string) => !isNaN(Number(v)))
            ) {
              return (
                <>
                  <div>{convertDate({ info: tempData as string, t })}</div>
                  {Array.isArray(newAge2) ? (
                    <div>{ageToString(newAge2, t)}</div>
                  ) : (
                    ""
                  )}
                </>
              );
            }
          }
          return "";
        case "PRINT":
          return (
            <PrintIcon
              onClick={() =>
                openPrinterDialog(
                  { sectionType: section, data, type },
                  {
                    setStaValues,
                    setStationeryModal,
                    setMsg,
                    closeMsg,
                    logoutAction,
                  },
                )
              }
            />
          );
        case "EST1":
          return (
            <CheckCircleIcon
              style={{
                color: statusColor(tempData),
              }}
            />
          );
        case "EST2":
          return (
            <PaidIcon
              onClick={() => openRowInfo(data, "DIN")}
              style={{
                color: statusColor(tempData),
              }}
            />
          );
        case "RUT":
          return routesList[tempData]?.NOMRUT ?? "";
        case "PREPCO":
          return tempData;
        case "TIP":
          switch (tempData) {
            case 1:
            case 5:
              return (
                <DesignServicesIcon
                  style={{
                    color: tempData === 5 ? colors.green : colors.orange,
                  }}
                />
              );
            case 2:
              return <NumbersIcon style={{ color: colors.blue }} />;
            case 3:
              return <InventoryIcon style={{ color: colors.green }} />;
            case 4:
              return <EventIcon style={{ color: colors.green }} />;
            case 6:
              return <ContentPasteIcon style={{ color: colors.darkGrey }} />;
            default:
              return "";
          }
        case "GEN":
          let sexValue = sexesOptions.filter(
            (e) => e.value === tempData.toString(),
          );
          return sexValue.length === 1 ? t(sexValue[0].label) : "";
        case "WHA":
          return !!tempData ? (
            <CheckBoxIcon style={{ color: colors.green }} />
          ) : (
            <CheckBoxOutlineBlankIcon style={{ color: colors.grey }} />
          );
        case "WEB":
          return !!tempData ? (
            <LanguageIcon style={{ color: colors.lightBlue }} />
          ) : (
            ""
          );
        case "AGE":
          return `ANO${section.code}` in data
            ? data[`ANO${section.code}`]
            : tempData in usersList
              ? usersList[tempData.toString()].NOMUSU
              : "";
        case "TAR":
          return (
            pricesRates.find((e: stdObject) => e.CODTAR === tempData)?.NOMTAR ??
            tempData
          );
        case "PSE":
          return tempData in insurancesList
            ? insurancesList[tempData].NOMPSE
            : "";
        case "PAG":
        case "NOMPAG":
          return tempData in statusDict ? statusDict[tempData].NOMPAG : "";
      }
    }
    return tempData;
  };

  return (
    <div ref={scrollDiv} onScroll={handleScroll} className="listBox">
      <table cellSpacing={0} cellPadding={5} width={"100%"}>
        <thead>
          {
            <tr className="header">
              {mainHeader.map((e: any, index: number) => (
                <th
                  key={index}
                  className="noSelect"
                  style={{
                    backgroundColor: colors[type],
                    width: e.ANCCAB > 0 ? e.ANCCAB : null,
                  }}
                >
                  {t(
                    `c.${getRealCode(
                      e.CODCAB,
                      typeof section.code === "string" ? section.code : "",
                    )}`,
                  )}
                </th>
              ))}
            </tr>
          }
        </thead>
        <tbody>
          {boxList.map((e: stdObject, index: number) => (
            <BoxRow
              e={e}
              key={index}
              type={section.code}
              index={index}
              statusDict={statusDict}
              mainHeader={mainHeader}
              openRowInfo={openRowInfo}
              writeInfo={writeInfo}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
